import stickyStyles from './StickyHeader.module.scss';
import eyecatcherStyles from './Eyecatcher.module.scss';
import conditionStyles from './ConditionInfo.module.scss';
import { useLocalStorage, useMedia } from '@components/hooks';
import styles from '@components/pages/Product/Product.module.scss';
import Container from '@components/components/Container/Container';
import ProductCardNonBuyableLegacy from '@components/components/ProductComponentsLegacy/ProductCardNonBuyable/ProductCardNonBuyable';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import classNames from 'classnames';
import {
  Button,
  Content,
  FontAwesomeProIcon,
  Heading,
  Image,
  Link,
  Modal,
} from '@wienenergiegithub/ui-next';
import { DialogDisclosure } from '@ariakit/react';

export const Icon = ({ small, image, title, icon }) => (
  <div className={styles.iconContainer}>
    {!!image && (
      <Image
        alt={title}
        className={classNames(styles.icon, {
          [styles.smallIcon]: small,
        })}
        src={image}
        title={title}
      />
    )}
    {!!icon && (
      <FontAwesomeProIcon
        className={classNames(styles.titleIcon, {
          [styles.smallIcon]: small,
        })}
        icon={icon}
        role="presentation"
        type="regular"
      />
    )}
  </div>
);

const ProductContext = createContext();

export const ProductProvider = ({ value, children }) => (
  <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
);

export const useProduct = () => useContext(ProductContext);

export const StickyHeader = ({ ctaButtonData, title, iconData, isOko }) => {
  const STICKY_OFFSET_MOBILE = 500;

  const [scrollState, setScrollState] = useState('initial');
  const isMobile = useMedia('(max-width: 767px)');
  const stickyPoint = isMobile
    ? STICKY_OFFSET_MOBILE
    : 2 * STICKY_OFFSET_MOBILE; // The Sticky Header triggers after Basisdaten

  useEffect(() => {
    // https://stackoverflow.com/questions/16239520/chrome-remembers-scroll-position
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    let ticking = false;
    let prevScrollpos = window.pageYOffset;

    const addScrollClasses = () => {
      const currentScrollPos = window.pageYOffset;
      if (prevScrollpos === currentScrollPos) {
        return;
      }
      if (prevScrollpos > currentScrollPos) {
        if (currentScrollPos <= stickyPoint) {
          setScrollState('initial');
          prevScrollpos = currentScrollPos;
          return;
        }
        setScrollState('upScrolled');
      } else if (currentScrollPos >= stickyPoint) {
        setScrollState('scrolled');
      }
      prevScrollpos = currentScrollPos;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          addScrollClasses(prevScrollpos);
          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile, scrollState]);

  return (
    <Container
      className={classNames(stickyStyles.stickyContainer, {
        [stickyStyles.containerUpScrolled]: scrollState === 'upScrolled',
        [stickyStyles.containerScrolled]: scrollState === 'scrolled',
        [stickyStyles.isOko]: isOko,
      })}
      size="full"
    >
      <Container
        className={stickyStyles.stickyWrapper}
        size={isMobile ? 'full' : 'xl'}
      >
        {!isMobile && (
          <div className={stickyStyles.titleWrapper}>
            <Icon {...iconData} small />
            <Heading noSpacing size="h1">
              {title}
            </Heading>
          </div>
        )}
        <div
          className={classNames(stickyStyles.stickyButton, {
            [stickyStyles.stickyButtonOko]: isOko,
          })}
        >
          <ButtonDecider headerButtonMobile={isMobile} {...ctaButtonData} />
        </div>
      </Container>
    </Container>
  );
};

export const EyeCatcher = ({ eyecatcher, dialog }) => {
  if (!eyecatcher?.text) {
    return null;
  }

  return (
    <Content
      as="div"
      className={classNames(
        eyecatcherStyles.eyecatcher,
        eyecatcherStyles[eyecatcher.theme],
      )}
      size="md"
    >
      {eyecatcher.text}
      {!!eyecatcher?.legalText && (
        <>
          <DialogDisclosure
            aria-label="Mehr Informationen über die Aktion"
            className={classNames(
              eyecatcherStyles.informationButton,
              eyecatcherStyles.promoDialogButton,
            )}
            store={dialog}
          >
            <FontAwesomeProIcon
              className={eyecatcherStyles.promoDialogIcon}
              fixedWidth
              icon="info-circle"
              role="presentation"
              type="regular"
            />
          </DialogDisclosure>
          <Modal header={eyecatcher.text} store={dialog}>
            <Content withParser>{eyecatcher.legalText}</Content>
          </Modal>
        </>
      )}
    </Content>
  );
};

export const ButtonDecider = ({
  title,
  headerButtonMobile,
  oko,
  showOko,
  noOkoCondition,
  customerType,
  phone,
  isMobile,
  orderDialog,
  newCustomerUrls,
  newCustomerUrl,
  existingCustomerUrl,
  configurationUrl,
  productType,
  productKey,
  isSpotProduct = false,
}) => {
  const [storage] = useLocalStorage('address-check');
  const [finalUrl, setFinalUrl] = useState('');

  useEffect(() => {
    let olavKeysforURL = '';
    let olavKeys = {};
    if (storage?.ID) {
      olavKeys = storage?.ID;
      olavKeysforURL = `${olavKeys?.gebaeudeKey}/${olavKeys?.gebaeudeKeyIdent}/${olavKeys?.anlageAdressKey}/${olavKeys?.anlageKey}`;
    } else {
      olavKeysforURL = storage?.currentAddress;
    }

    // options is always empty -> thats a specific requirement from ticket KITT3-3293
    const newUrlOptions = `?tarif=[key:${productKey}]&newAddress=${olavKeysforURL}`;

    setFinalUrl(newCustomerUrl + newUrlOptions);
  }, [storage]);

  return (
    <>
      {productType === 'superschnell' && (
        <Button
          isElevated
          render={
            <Link
              className={styles.button}
              to={storage?.Internet === 'JA' ? finalUrl : configurationUrl.url}
            >
              {configurationUrl?.title}
            </Link>
          }
        />
      )}
      {oko && phone && showOko && (
        <ProductCardNonBuyableLegacy
          small={isMobile}
          standalone
          subtitle="Jetzt telefonisch anmelden"
          title="Öko-Option telefonisch anmelden"
        />
      )}
      {noOkoCondition && customerType === 'all' && (
        <DialogDisclosure
          render={
            <Button className={styles.button} isElevated>
              {headerButtonMobile ? `${title} anmelden` : 'Tarif anmelden'}
            </Button>
          }
          store={orderDialog}
        />
      )}
      {noOkoCondition &&
        customerType === 'newcustomers' &&
        newCustomerUrls?.length > 0 && (
          <div className={styles.ctaWrapper}>
            <DialogDisclosure
              render={
                <Button className={styles.button} isElevated>
                  Tarif anmelden (für Neukund*innen)
                </Button>
              }
              store={orderDialog}
            />
          </div>
        )}
      {noOkoCondition &&
        customerType === 'newcustomers' &&
        !newCustomerUrls?.length && (
          <Button
            isElevated
            render={
              <Link className={styles.button} to={newCustomerUrl}>
                Tarif anmelden (für Neukund*innen)
              </Link>
            }
          />
        )}
      {!!noOkoCondition &&
        customerType === 'existingcustomers' &&
        !isSpotProduct && (
          <Button
            isElevated
            render={
              <Link className={styles.button} to={existingCustomerUrl}>
                Tarif anmelden (für Bestandskund*innen)
              </Link>
            }
          />
        )}
      {isSpotProduct && (
        <Button
          isElevated
          render={
            <Link className={styles.button} to={existingCustomerUrl}>
              Tarif wechseln
            </Link>
          }
        />
      )}
    </>
  );
};

export const ConditionsInfo = ({ heading, subtitle, content }) => {
  const initialRender = useRef(true);

  const [animationState, setAnimationState] = useState(true);

  // trigger animation everytime the value change
  useEffect(() => {
    let timer = null;
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      if (animationState) {
        setAnimationState(false);
      }
      timer = setTimeout(() => {
        setAnimationState(true);
      }, 1);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [subtitle]);
  return (
    <div
      className={classNames(conditionStyles.conditionsInfos, {
        [conditionStyles.animation]: animationState,
      })}
      key={`konditionen-${heading}`}
    >
      <Heading className={conditionStyles.infoHeading} size="h6">
        {heading}
      </Heading>
      <div className={conditionStyles.infoItems}>
        <Content className={conditionStyles.infoContent}>{subtitle}</Content>
        <Content as="div" noSpacing size="md" withParser>
          {content}
        </Content>
      </div>
    </div>
  );
};
